import type { ReactNode } from "react";
import { motion } from "motion/react";
import { cn } from "./cn";
import { MARK } from "./Mark";
import { bouncy, softSpring } from "./motion";

const C = {
  primary: "var(--primary)",
  deep: "var(--primary-ink)",
  tint: "var(--primary-tint)",
  cream: "var(--cream)",
  night: "var(--accent)",
  paper: "var(--surface)",
  rule: "var(--accent-soft)",
  edge: "var(--line-strong)",
};

const ITALIC = (cx: number, cy: number) => `translate(${cx} ${cy}) skewX(-12) translate(${-cx} ${-cy})`;

type SparkProps = { x: number; y: number; size: number; tone?: string; dot?: string | false; rotate?: number; delay?: number; skip?: number };

/**
 * The mark at any size, centred on x and y, snapping in after `delay`. `tone` fills the left bar, `dot` the
 * right bar (`false` uses `tone` for it too). `skip` leaves out one piece by index.
 */
function Spark({ x, y, size, tone = C.primary, dot = C.night, rotate = 0, delay = 0.15, skip }: SparkProps) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <motion.g initial={{ scale: 0, rotate: rotate - 30 }} animate={{ scale: 1, rotate }} transition={{ ...bouncy, delay }}>
        <g transform={`scale(${size / 40}) translate(-20 -20)`}>
          {MARK.parts.map((p, i) => (i === skip ? null : <path key={p.d} d={p.d} fill={i === 1 && dot ? dot : tone} />))}
        </g>
      </motion.g>
    </g>
  );
}

/** The slanted square every scene stands on. */
function Panel({ fill = C.cream, children }: { fill?: string; children?: ReactNode }) {
  return (
    <motion.g initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={softSpring}>
      <g transform={ITALIC(80, 54)}>
        <rect x="38" y="12" width="84" height="84" fill={fill} />
        {children}
      </g>
    </motion.g>
  );
}

/** Pops a piece in from `from` once the panel lands. */
function Enter({ from, delay = 0.06, children }: { from: Record<string, number>; delay?: number; children: ReactNode }) {
  return (
    <motion.g initial={{ opacity: 0, ...from }} animate={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 }} transition={{ ...bouncy, damping: 15, delay }}>
      {children}
    </motion.g>
  );
}

function Night() {
  return (
    <>
      <Panel fill={C.night} />
      <rect x="22" y="94" width="100" height="7" fill={C.primary} />
      <Enter from={{ rotate: -40, scale: 0.6 }}>
        <path d="M70 26 A22 22 0 1 0 70 70 A28 28 0 0 1 70 26 Z" fill={C.tint} transform="rotate(-24 70 48)" />
      </Enter>
      <Spark x={103} y={36} size={22} dot={C.paper} delay={0.2} />
      <Spark x={94} y={74} size={12} tone={C.tint} dot={false} rotate={20} delay={0.3} />
      <Spark x={60} y={22} size={8} tone={C.primary} dot={false} rotate={-10} delay={0.36} />
    </>
  );
}

function Plane() {
  return (
    <>
      <Panel />
      <path d="M22 104 Q38 82 64 78" fill="none" stroke={C.tint} strokeWidth="4" strokeDasharray="7 6" />
      <Enter from={{ x: -24, y: 20, rotate: -12 }}>
        <polygon points="136,22 48,60 86,68" fill={C.primary} />
        <polygon points="136,22 86,68 78,94" fill={C.deep} />
        <polygon points="48,60 86,68 70,74" fill={C.tint} />
      </Enter>
      <Spark x={66} y={32} size={16} rotate={-14} delay={0.3} />
    </>
  );
}

function Search() {
  return (
    <>
      <Panel>
        <rect x="50" y="26" width="44" height="8" fill={C.tint} />
        <rect x="50" y="42" width="30" height="8" fill={C.tint} />
        <rect x="50" y="58" width="36" height="8" fill={C.tint} />
      </Panel>
      <Enter from={{ x: 18, y: 12, rotate: 12 }}>
        <rect x="-5.5" y="0" width="11" height="34" fill={C.night} transform="translate(115 80) rotate(-45)" />
        <circle cx="98" cy="62" r="23" fill={C.paper} stroke={C.deep} strokeWidth="8" />
      </Enter>
      <Spark x={98} y={62} size={28} delay={0.24} />
    </>
  );
}

function Saved() {
  return (
    <>
      <Panel />
      <Enter from={{ y: 14 }}>
        <rect x="44" y="22" width="66" height="78" fill={C.paper} stroke={C.edge} strokeWidth="1.5" />
        <rect x="53" y="32" width="14" height="14" fill={C.tint} />
        <rect x="53" y="56" width="32" height="6" fill={C.rule} />
        <rect x="53" y="68" width="46" height="6" fill={C.rule} />
        <rect x="53" y="80" width="38" height="6" fill={C.rule} />
      </Enter>
      <Enter from={{ y: -26 }} delay={0.14}>
        <path d="M90 14 H118 V66 L104 54 L90 66 Z" fill={C.primary} />
      </Enter>
      <Spark x={104} y={32} size={15} tone={C.paper} delay={0.32} />
    </>
  );
}

function Lost() {
  return (
    <>
      <Panel>
        <g transform="translate(66 50) scale(1.6) rotate(-62 29 21)">
          <path d={MARK.parts[1].d} fill={C.tint} />
        </g>
      </Panel>
      <Spark x={70} y={54} size={68} skip={1} delay={0.08} />
      <rect x="22" y="104" width="116" height="3" fill={C.tint} />
      <Enter from={{ y: -50, rotate: -50 }} delay={0.3}>
        <rect x="-30" y="-5" width="60" height="10" fill={C.primary} transform="translate(104 98) rotate(-8)" />
      </Enter>
    </>
  );
}

const SCENES = { night: Night, plane: Plane, search: Search, saved: Saved, lost: Lost };

export type IllustrationName = keyof typeof SCENES;

/**
 * Flat, square-edged scenes built from the mark for empty states: `night` (caught up), `plane` (nothing sent),
 * `search` (no results), `saved` (nothing saved), `lost` (404). Width comes from `className`.
 */
export function Illustration({ name, className }: { name: IllustrationName; className?: string }) {
  const Scene = SCENES[name];
  return (
    <svg viewBox="0 0 160 120" aria-hidden className={cn("block h-auto w-40 overflow-visible", className)}>
      <Scene />
    </svg>
  );
}
